import { Plus, Trash2, Eye, EyeOff, Layers, Palette, ChevronRight, LayoutGrid, Type, ShieldAlert, Target } from "lucide-react";
import type { MapStyleLayersPaneProps, MapStyleLayerPresentationItem } from "../../contracts/map-workspace";
import { cn } from "../../lib/utils";
import { Label } from "../../internal/primitives/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../internal/primitives/tabs";
import { MapEditorEmptyState } from "./internal/MapEditorKitParts";
import React from "react";

/**
 * MapStyleLayersPane
 * Renders the mounted source-layer target inventory and the selected target's presentation inventory
 * in one continuous operator-facing surface.
 */
export function MapStyleLayersPane({
  presentationTemplates,
  presentations,
  currentTargetId,
  emptyPresentationsMessage = "No presentations defined for this target.",
  readOnly = false,
  editorContent,
  onAddPresentation,
  onSelectPresentation,
  onTogglePresentationEnabled,
  onRemovePresentation,
  className,
}: MapStyleLayersPaneProps) {
  const selectedPresentation = presentations.find((p) => p.selected);
  const selectedPresentationId = selectedPresentation?.id ?? null;
  const selectedPresentationRole = selectedPresentation?.role ?? null;

  // Manage active tab state internally to allow user navigation while keeping context
  const [activeTab, setActiveTab] = React.useState<string>("base");

  // Sync active tab with selected presentation role when selection changes
  React.useEffect(() => {
    if (selectedPresentationRole) {
      const roleToTab: Record<string, string> = {
        base: "base",
        overlay_badge: "badges",
        overlay_label: "labels",
        overlay_symbol: "symbols",
      };
      setActiveTab(roleToTab[selectedPresentationRole] || "base");
    }
  }, [selectedPresentationId, selectedPresentationRole]);

  const basePresentations = presentations.filter((p) => p.role === "base");
  const badgePresentations = presentations.filter((p) => p.role === "overlay_badge");
  const labelPresentations = presentations.filter((p) => p.role === "overlay_label");
  const symbolPresentations = presentations.filter((p) => p.role === "overlay_symbol");

  // Synchronize selection state to ensure the host provides editor data for the base style.
  // This allows the base editor to appear "inline and immediately" when the tab is active.
  React.useEffect(() => {
    const basePresentation = basePresentations[0];
    if (
      activeTab === "base" &&
      basePresentation &&
      (!editorContent || selectedPresentationId !== basePresentation.id) &&
      !readOnly
    ) {
      onSelectPresentation?.(basePresentation.id);
    }
  }, [
    activeTab,
    basePresentations,
    editorContent,
    onSelectPresentation,
    readOnly,
    selectedPresentationId,
  ]);

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      {/* 2. Presentations & Editor Content Section */}
      <div className="flex min-h-0 flex-1 flex-col">
        {!currentTargetId ? (
          <div className="flex flex-1 items-center justify-center p-8">
            <MapEditorEmptyState
              icon={<Target className="h-8 w-8" />}
              title="No target selected"
              message={
                emptyPresentationsMessage ||
                "Select a source layer to manage its style presentations."
              }
            />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 px-4 pt-4">
              <TabsList className="scrollbar-none flex h-auto w-full justify-start gap-4 overflow-x-auto rounded-none border-b bg-transparent p-0">
                <InventoryTabTrigger value="base" label="Base" icon={<Layers className="h-3.5 w-3.5" />} />
                <InventoryTabTrigger
                  value="badges"
                  label="Badges"
                  count={badgePresentations.length}
                  icon={<ShieldAlert className="h-3.5 w-3.5" />}
                />
                <InventoryTabTrigger
                  value="labels"
                  label="Labels"
                  count={labelPresentations.length}
                  icon={<Type className="h-3.5 w-3.5" />}
                />
                <InventoryTabTrigger
                  value="symbols"
                  label="Symbols"
                  count={symbolPresentations.length}
                  icon={<LayoutGrid className="h-3.5 w-3.5" />}
                />
              </TabsList>
            </div>

            <div className="scrollbar-thin scrollbar-thumb-muted min-h-0 flex-1 overflow-y-auto px-4 py-5">
              {/* Base Tab - Immediate Inline Editor */}
              <TabsContent value="base" className="mt-0 focus-visible:ring-0">
                {basePresentations.length > 0 ? (
                  <div className="animate-in fade-in duration-200">
                    {editorContent}
                  </div>
                ) : (
                  <MapEditorEmptyState message="No base style defined." className="py-6" />
                )}
              </TabsContent>

              {/* Badges Tab */}
              <TabsContent value="badges" className="mt-0 space-y-4 focus-visible:ring-0">
                <InventorySectionHeader
                  title="Status Badges"
                  description="Indicators and alerts shown over features."
                  onAdd={
                    !readOnly && onAddPresentation && currentTargetId
                      ? () => {
                          const template =
                            presentationTemplates.find((t) => t.id.includes("badge")) ||
                            presentationTemplates[0];
                          onAddPresentation(currentTargetId, template?.id || "overlay_badge");
                        }
                      : undefined
                  }
                  addLabel="Add Badge"
                />
                <PresentationGrid
                  presentations={badgePresentations}
                  emptyMessage="No overlay badges defined."
                  readOnly={readOnly}
                  onSelect={onSelectPresentation}
                  onToggleEnabled={onTogglePresentationEnabled}
                  onRemove={onRemovePresentation}
                />
                {activeTab === "badges" && selectedPresentationRole === "overlay_badge" && editorContent && (
                  <div className="animate-in fade-in slide-in-from-top-2 mt-6 border-t border-border/60 pt-6 duration-300">
                    {editorContent}
                  </div>
                )}
              </TabsContent>

              {/* Labels Tab */}
              <TabsContent value="labels" className="mt-0 space-y-4 focus-visible:ring-0">
                <InventorySectionHeader
                  title="Text Labels"
                  description="Annotated fields and identifiers."
                  onAdd={
                    !readOnly && onAddPresentation && currentTargetId
                      ? () => {
                          const template =
                            presentationTemplates.find((t) => t.id.includes("label")) ||
                            presentationTemplates[0];
                          onAddPresentation(currentTargetId, template?.id || "overlay_label");
                        }
                      : undefined
                  }
                  addLabel="Add Label"
                />
                <PresentationGrid
                  presentations={labelPresentations}
                  emptyMessage="No overlay labels defined."
                  readOnly={readOnly}
                  onSelect={onSelectPresentation}
                  onToggleEnabled={onTogglePresentationEnabled}
                  onRemove={onRemovePresentation}
                />
                {activeTab === "labels" && selectedPresentationRole === "overlay_label" && editorContent && (
                  <div className="animate-in fade-in slide-in-from-top-2 mt-6 border-t border-border/60 pt-6 duration-300">
                    {editorContent}
                  </div>
                )}
              </TabsContent>

              {/* Symbols Tab */}
              <TabsContent value="symbols" className="mt-0 space-y-4 focus-visible:ring-0">
                <InventorySectionHeader
                  title="Overlay Symbols"
                  description="Secondary icons and sprite markers."
                  onAdd={
                    !readOnly && onAddPresentation && currentTargetId
                      ? () => {
                          const template =
                            presentationTemplates.find((t) => t.id.includes("symbol")) ||
                            presentationTemplates[0];
                          onAddPresentation(currentTargetId, template?.id || "overlay_symbol");
                        }
                      : undefined
                  }
                  addLabel="Add Symbol"
                />
                <PresentationGrid
                  presentations={symbolPresentations}
                  emptyMessage="No overlay symbols defined."
                  readOnly={readOnly}
                  onSelect={onSelectPresentation}
                  onToggleEnabled={onTogglePresentationEnabled}
                  onRemove={onRemovePresentation}
                />
                {activeTab === "symbols" && selectedPresentationRole === "overlay_symbol" && editorContent && (
                  <div className="animate-in fade-in slide-in-from-top-2 mt-6 border-t border-border/60 pt-6 duration-300">
                    {editorContent}
                  </div>
                )}
              </TabsContent>
            </div>
          </Tabs>
        )}
      </div>
    </div>
  );
}

function InventoryTabTrigger({
  value,
  label,
  count,
  icon,
}: {
  value: string;
  label: string;
  count?: number;
  icon: React.ReactNode;
}) {
  return (
    <TabsTrigger
      value={value}
      className="group flex items-center gap-2 rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 transition-all data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
    >
      <div className="text-muted-foreground transition-colors group-data-[state=active]:text-primary">
        {icon}
      </div>
      <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground transition-colors group-data-[state=active]:text-foreground">
        {label}
      </span>
      {typeof count === "number" && count > 0 && (
        <span className="rounded-full border border-border/30 bg-muted px-1.5 py-0.5 text-[9px] font-bold text-muted-foreground transition-colors group-data-[state=active]:bg-primary/10 group-data-[state=active]:text-primary">
          {count}
        </span>
      )}
    </TabsTrigger>
  );
}

function InventorySectionHeader({
  title,
  description,
  onAdd,
  addLabel,
}: {
  title: string;
  description: string;
  onAdd?: () => void;
  addLabel?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-col gap-0.5">
        <Label className="text-[10px] font-black uppercase leading-none tracking-widest text-muted-foreground/80">
          {title}
        </Label>
        <p className="text-[10px] italic text-muted-foreground/60">{description}</p>
      </div>
      {onAdd && addLabel && (
        <button
          type="button"
          onClick={onAdd}
          className="flex items-center gap-1.5 rounded border border-primary/20 px-2.5 py-1.5 text-[10px] font-bold text-primary shadow-sm transition-all active:scale-95 hover:bg-primary/5"
        >
          <Plus className="h-3.5 w-3.5" /> {addLabel}
        </button>
      )}
    </div>
  );
}

function PresentationGrid({
  presentations,
  emptyMessage,
  readOnly,
  onSelect,
  onToggleEnabled,
  onRemove,
}: {
  presentations: MapStyleLayerPresentationItem[];
  emptyMessage: string;
  readOnly: boolean;
  onSelect?: (id: string) => void;
  onToggleEnabled?: (id: string, enabled: boolean) => void;
  onRemove?: (id: string) => void;
}) {
  if (presentations.length === 0) return <MapEditorEmptyState message={emptyMessage} className="py-8" />;
  return (
    <div className="grid gap-2">
      {presentations.map((pres) => (
        <PresentationCard
          key={pres.id}
          pres={pres}
          readOnly={readOnly}
          onClick={() => onSelect?.(pres.id)}
          onToggleEnabled={(enabled) => onToggleEnabled?.(pres.id, enabled)}
          onRemove={() => onRemove?.(pres.id)}
        />
      ))}
    </div>
  );
}

function PresentationCard({
  pres,
  readOnly,
  onClick,
  onToggleEnabled,
  onRemove,
}: {
  pres: MapStyleLayerPresentationItem;
  readOnly: boolean;
  onClick: () => void;
  onToggleEnabled: (enabled: boolean) => void;
  onRemove: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "group relative flex cursor-pointer items-center gap-3 rounded-lg border bg-card p-2.5 transition-all shadow-sm overflow-hidden",
        pres.selected
          ? "border-primary/40 bg-primary/5 ring-1 ring-primary/20"
          : "hover:border-primary/20 hover:bg-muted/30"
      )}
    >
      {pres.selected && <div className="absolute bottom-0 left-0 top-0 w-0.5 bg-primary" />}
      <div className="relative shrink-0">
        {pres.swatchColor ? (
          <div
            className="h-8 w-8 rounded border border-background shadow-sm"
            style={{ backgroundColor: pres.swatchColor }}
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded border border-dashed border-muted bg-muted/20">
            <Palette className="h-3.5 w-3.5 text-muted-foreground/30" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              "truncate text-[11px] font-bold transition-colors",
              pres.selected ? "text-primary" : "text-foreground"
            )}
          >
            {pres.label}
          </span>
          {pres.roleLabel && (
            <span className="rounded border border-border/10 bg-muted/80 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-tight text-muted-foreground/60">
              {pres.roleLabel}
            </span>
          )}
        </div>
        {pres.summary && (
          <div className="mt-0.5 line-clamp-1 text-[10px] font-medium italic text-muted-foreground/60">
            {pres.summary}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1 px-1">
        {!readOnly && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleEnabled(!pres.enabled);
              }}
              className={cn(
                "rounded p-1.5 transition-all",
                pres.enabled ? "text-primary hover:bg-primary/10" : "text-muted-foreground/20 hover:bg-muted"
              )}
              title={pres.enabled ? "Disable" : "Enable"}
            >
              {pres.enabled ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            </button>
            {pres.removable && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="rounded p-1.5 text-muted-foreground/20 transition-all hover:bg-destructive/10 hover:text-destructive"
                title="Remove"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        )}
        <ChevronRight
          className={cn(
            "h-4 w-4 transition-all",
            pres.selected ? "translate-x-0.5 text-primary" : "text-muted-foreground/20 group-hover:text-primary/50"
          )}
        />
      </div>
    </div>
  );
}
